import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { useHapticFeedback } from '@blade/ui-kit'
import { useAppRoutes } from '@root/features/routing'
import { useTranslation } from '@root/shared/localization'
import { RectangleNetworkBackground_ } from '@root/shared/ui/background/rectangle-network'
import { ButtonPurchase } from '@root/shared/ui-kit'

import { PremiumSlider } from '../../premium-slider'
import { userPremiumExpiredStateAtom } from '../../user-premium'

import { ProfilePremiumListHeader } from './profile-premium-list-header'
import { ProfilePremiumListPast } from './profile-premium-list-past'

export const ProfilePremiumListEmpty = () => {
  const { t } = useTranslation()
  const { navigateToPremiumIndex } = useAppRoutes()
  const haptics = useHapticFeedback()

  const [expiredPremiumsState] = useAtom(userPremiumExpiredStateAtom)

  const handleExplore = () => {
    haptics.impactOccurred('light')
    navigateToPremiumIndex()
  }

  const pastPurchases = AsyncStateN.matchWaiting(expiredPremiumsState, {
    Waiting: () => [],
    Error: () => [],
    Success: (pastPurchases) => pastPurchases,
  })

  if (pastPurchases.length === 0) {
    return (
      <Root>
        <ProfilePremiumListHeader />
        <Footer>
          <ProfilePremiumListEmptySlider mode="single" />
          <ActionGroup>
            <ButtonPurchase
              fullWidth
              product="premium"
              type="button"
              radius="rounded"
              onClick={handleExplore}
            >
              {t('profile.buy_d_premium')}
            </ButtonPurchase>
          </ActionGroup>
        </Footer>
        <GlowingStartEmpty />
        <GlowingEndEmpty />
      </Root>
    )
  }

  return (
    <Root aria-disabled>
      <ProfilePremiumListHeader noActivePremium />
      <ProfilePremiumListPast pastPurchases={pastPurchases} />
      <GlowingEndPast />
    </Root>
  )
}

const Root = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: 1fr 1fr;

  position: relative;
  overflow: hidden;

  ${RectangleNetworkBackground_}
  background-repeat: no-repeat;
  background-position: 0% 100%;

  &[aria-disabled] {
    background-position: 0% 20%;
  }

  box-shadow: inset 4px 14px 60px var(--appearance-1);
`

const Footer = styled.div`
  display: grid;
  min-height: 0;
  grid-template-rows: 1fr auto;

  position: relative;
  z-index: 1;
`

const ProfilePremiumListEmptySlider = styled(PremiumSlider)`
  --slider-title-color: var(--textA-9);
  --slider-title-weight: 500;
  --slider-image-width: 90%;
  --slider-width: 100%;

  display: grid;
  margin: 0 auto;
  min-height: 233px;

  object {
    margin-top: -10px;
  }
`

const ActionGroup = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 6px;
  padding-top: 12px;

  padding-block: 8px var(--safe-bottom);
  padding-inline: 12px;
`

const GlowingStartEmpty = styled.span`
  background-image: radial-gradient(
    50% 370px at 110px 60%,
    var(--subscriptionGradientFromA-4) 0%,
    transparent 100%
  );
  pointer-events: none;
  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  opacity: 0.2;

  &::before {
    content: '';

    opacity: 0.7;
    position: absolute;

    top: 25%;
    left: -15%;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(
      50% 25% at 50% 70%,
      var(--subscriptionGradientFromA-9),
      transparent
    );

    transform: rotate(45deg);
  }
`

const GlowingEndEmpty = styled.span`
  background-image: radial-gradient(
    50% 60% at 85% 100%,
    var(--subscriptionGradientToA-3) 0%,
    transparent 100%
  );
  pointer-events: none;

  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  opacity: 1;

  &::before {
    content: '';
    opacity: 0.6;
    position: absolute;

    top: -31%;
    left: -20%;
    width: 200%;
    height: 130%;
    background-image: radial-gradient(
      50% 25% at 50% 70%,
      var(--subscriptionGradientFromA-8),
      transparent
    );
    transform: rotate(45deg);
  }
`

const GlowingEndPast = styled.span`
  background-image: radial-gradient(
    50% 30% at 31% 13%,
    var(--subscriptionGradientFromA-5) 0%,
    transparent 100%
  );
  pointer-events: none;
  position: absolute;
  left: -10px;
  bottom: -270px;
  right: 0;
  top: 0;
  opacity: 0.8;

  &::before {
    content: '';
    opacity: 0.4;
    position: absolute;
    top: -40%;
    left: 6%;
    width: 90%;
    height: 100%;
    transform: rotate(344deg);
    background-image: radial-gradient(
      50% 25% at 50% 70%,
      var(--subscriptionGradientToA-6),
      transparent
    );
  }
`
